/**
 * generateCeremonyLayout: produces a starting ceremony arrangement consisting
 * of individual standalone_chair objects arranged in two sections (left and
 * right of a center aisle) plus an optional arbor at the focal end.
 *
 * DESIGN PRINCIPLES
 * -----------------
 * • Every chair is a real LayoutObject of type 'standalone_chair' — not a
 *   baked row. The coordinator can move, delete, or re-label individual chairs
 *   after generation exactly as if they had placed them by hand.
 *
 * • Each generated row is tagged with a shared rowGroupId. The editor uses this
 *   to offer "Select Row" — one click selects all chairs in that row so the
 *   coordinator can move or delete the whole row at once. Individual chairs
 *   remain independently editable after the group operation.
 *
 * • The algorithm is deterministic. Given the same inputs it always produces
 *   the same output; no randomness.
 *
 * • All placement uses world feet (the same coordinate system as every other
 *   object on the canvas).
 *
 * PLACEMENT LAYOUT
 * ----------------
 * Guests enter at y=0 (entrance). The focal end (arbor) is at high-y (far end).
 * Chair rows are placed working BACKWARD from the focal end — they cluster near
 * the arbor, as happens in a real ceremony. The first row (closest to the arbor)
 * starts at CEREMONY_FRONT_CLEARANCE_FT in front of the arbor; subsequent rows
 * step toward the entrance.
 *
 * ┌──────────────────────────────┐  y = 0  (entrance)
 * │                              │
 * │   row N  (farthest from      │
 * │           arbor, toward      │
 * │           entrance)          │
 * │   ...                        │
 * │   row 2                      │
 * │   row 1  (closest to arbor)  │
 * │                              │
 * │         [ ARBOR ]            │  y = roomL - FOCAL_MARGIN - arborH
 * └──────────────────────────────┘  y = roomL
 *
 * CONSTANTS TO TUNE AFTER VENUE TESTING
 * --------------------------------------
 * All spatial constants are exported so a coordinator or admin can adjust
 * them with a one-line change and without touching any other module.
 */

import type { LayoutObject, RoomDefinition, ObjectCatalogEntry } from '../types';

// ---------------------------------------------------------------------------
// Tuneable spatial constants
// ---------------------------------------------------------------------------

/** Gap between the two seating sections (total aisle width), in feet.
 *  Standard ceremony aisle is 4–5ft. */
export const CEREMONY_AISLE_WIDTH_FT = 4;

/** Center-to-center spacing between adjacent chairs within a row, in feet.
 *  Must match the CHAIR_ROW_SPACING_FT constant in ObjectLibraryPanel. */
export const CEREMONY_CHAIR_SPACING_FT = 1.8;

/** Front-to-back distance between chair rows (center-to-center), in feet.
 *  Also exported as CEREMONY_ROW_SPACING_FT per the naming in the brief. */
export const CEREMONY_ROW_DEPTH_FT = 2.5;
export const CEREMONY_ROW_SPACING_FT = CEREMONY_ROW_DEPTH_FT; // alias

/** Gap from each section's aisle edge to the center of the nearest chair, in feet.
 *  Keeps chairs from sitting flush against the aisle centerline. */
export const CEREMONY_SECTION_GAP_FT = 0.4;

/** Clear space between the front face of the arbor and the nearest chair row.
 *  This is the "front clearance" — the gap the officiant and couple stand in.
 *  Standard ceremony aisles leave 6–8ft here. */
export const CEREMONY_FRONT_CLEARANCE_FT = 6;

/** Space reserved at the focal end (high-y) between the room wall and the
 *  back of the arbor. Keeps the arbor off the back wall. */
export const CEREMONY_FOCAL_MARGIN_FT = 2;

/** Side margin from room walls to the outer edge of each seating section, in feet.
 *  Keeps chairs away from the walls so people can reach their seats. */
export const CEREMONY_SIDE_MARGIN_FT = 2;

/** Maximum chairs per row per section (capped to keep rows from hitting walls). */
const MAX_CHAIRS_PER_ROW_PER_SECTION = 8;

/** Minimum chairs per row per section. */
const MIN_CHAIRS_PER_ROW_PER_SECTION = 2;

// ---------------------------------------------------------------------------
// Input / output types
// ---------------------------------------------------------------------------

export interface CeremonyGenerationInput {
  /** Number of chairs to seat (guests — does not include officiant/couple). */
  chairCount: number;
  /** Whether to place an arbor at the focal end. */
  includeArbor: boolean;
  /** The room's geometry (width, length). */
  roomDefinition: RoomDefinition;
  /** Catalog entries for standalone_chair and arbor. */
  objectCatalog: ObjectCatalogEntry[];
}

// ---------------------------------------------------------------------------
// Main generator
// ---------------------------------------------------------------------------

/**
 * Returns a list of LayoutObjects representing a ceremony starting layout.
 * All coordinates are in feet using the room's x/y world space.
 */
export function generateCeremonyLayout(input: CeremonyGenerationInput): LayoutObject[] {
  const { chairCount, includeArbor, roomDefinition, objectCatalog } = input;
  const objects: LayoutObject[] = [];

  const roomW = roomDefinition.widthFt;
  const roomL = roomDefinition.lengthFt;

  // -----------------------------------------------------------------
  // 1. Place the arbor first — it anchors everything else.
  //    Arbor centered on x-axis, near the focal end (high-y).
  // -----------------------------------------------------------------
  const arborEntry = objectCatalog.find((e) => e.itemType === 'arbor');
  const arborH = arborEntry?.defaultHeightFt ?? 2;
  const arborW = arborEntry?.defaultWidthFt ?? 6;

  // Arbor back face sits FOCAL_MARGIN_FT from the back wall.
  // arborY = top-left y of the arbor (origin convention matches Arbor.tsx).
  const arborY = roomL - CEREMONY_FOCAL_MARGIN_FT - arborH;
  const arborX = roomW / 2 - arborW / 2; // centered on room x-axis

  if (includeArbor && arborEntry) {
    objects.push({
      id: crypto.randomUUID(),
      type: 'arbor',
      x: arborX,
      y: arborY,
      rotation: 0,
      label: 'Arbor',
      seats: null,
      locked: false,
      zIndex: 5,
    });
  }

  // -----------------------------------------------------------------
  // 2. Compute how many chairs fit per row per section.
  //    Available width for one section = half room − half aisle − gap − side margin.
  // -----------------------------------------------------------------
  const halfAisle = CEREMONY_AISLE_WIDTH_FT / 2;
  const sectionWidth = roomW / 2 - halfAisle - CEREMONY_SECTION_GAP_FT - CEREMONY_SIDE_MARGIN_FT;
  const rawChairsPerRow = Math.floor(sectionWidth / CEREMONY_CHAIR_SPACING_FT);
  const chairsPerRowPerSection = Math.min(
    MAX_CHAIRS_PER_ROW_PER_SECTION,
    Math.max(MIN_CHAIRS_PER_ROW_PER_SECTION, rawChairsPerRow)
  );

  // -----------------------------------------------------------------
  // 3. Split chairCount evenly left/right. Odd count → extra on left.
  // -----------------------------------------------------------------
  const chairsPerSectionTotal = Math.ceil(chairCount / 2);   // left
  const chairsRightTotal = Math.floor(chairCount / 2);

  const rowsNeeded = Math.ceil(chairsPerSectionTotal / chairsPerRowPerSection);

  // -----------------------------------------------------------------
  // 4. Determine where the FIRST (closest-to-arbor) row sits.
  //    First row front face = arborY − FRONT_CLEARANCE_FT.
  //    Chair centers are FRONT_CLEARANCE_FT in front of the arbor front face.
  //
  //    firstRowY = arborY - FRONT_CLEARANCE_FT
  //    Subsequent rows step toward the entrance (decreasing y).
  //    Clamp so no row goes above y=2 (never crowd the entrance).
  // -----------------------------------------------------------------
  const arborFrontFaceY = arborY; // top face of arbor rectangle
  const firstRowY = arborFrontFaceY - CEREMONY_FRONT_CLEARANCE_FT;

  // -----------------------------------------------------------------
  // 5. Place chairs row by row, from arbor end toward entrance.
  //    Row 0 = closest to arbor (firstRowY).
  //    Row rowsNeeded-1 = farthest from arbor (toward entrance).
  // -----------------------------------------------------------------
  const roomCenterX = roomW / 2;
  const leftInnerX = roomCenterX - halfAisle - CEREMONY_SECTION_GAP_FT;
  const rightInnerX = roomCenterX + halfAisle + CEREMONY_SECTION_GAP_FT;

  let leftPlaced = 0;
  let rightPlaced = 0;

  for (let row = 0; row < rowsNeeded; row++) {
    const y = firstRowY - row * CEREMONY_ROW_DEPTH_FT;

    // Clamp: never place chairs at y < 2 (too close to entrance)
    if (y < 2) break;

    // Chairs in this row (both sides) share a rowGroupId
    const rowGroupId = `row-${crypto.randomUUID()}`;

    // How many chairs go in this row for each section?
    const leftInRow = Math.min(chairsPerRowPerSection, chairsPerSectionTotal - leftPlaced);
    const rightInRow = Math.min(chairsPerRowPerSection, chairsRightTotal - rightPlaced);

    // Left section chairs (innermost to outermost, left of aisle)
    for (let c = 0; c < leftInRow; c++) {
      objects.push(makeChair(
        leftInnerX - c * CEREMONY_CHAIR_SPACING_FT,
        y,
        rowGroupId,
        objectCatalog
      ));
    }
    leftPlaced += leftInRow;

    // Right section chairs (innermost to outermost, right of aisle)
    for (let c = 0; c < rightInRow; c++) {
      objects.push(makeChair(
        rightInnerX + c * CEREMONY_CHAIR_SPACING_FT,
        y,
        rowGroupId,
        objectCatalog
      ));
    }
    rightPlaced += rightInRow;
  }

  return objects;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function makeChair(
  x: number,
  y: number,
  rowGroupId: string,
  objectCatalog: ObjectCatalogEntry[]
): LayoutObject {
  const chairEntry = objectCatalog.find((e) => e.itemType === 'standalone_chair');
  return {
    id: crypto.randomUUID(),
    type: 'standalone_chair',
    x: Math.round(x * 100) / 100,
    y: Math.round(y * 100) / 100,
    rotation: 0,
    label: 'Chair',
    seats: chairEntry?.defaultSeats ?? 1,
    locked: false,
    zIndex: 4,
    rowGroupId,
  };
}
